import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { PageStyled } from '@mobi/account/Common/Styles'
import { useHostContextFeature } from '@mobi/account/Hooks'
import { BetAccount, InitialData } from '@mobi/api-types'
import { PaymentMethods } from '@mobi/component-library/Areas/Deposit/Payment'
import { isSelfServePaymentMethod } from '@mobi/component-library/Areas/Deposit/helpers'
import { type PaymentMethodsAll } from '@mobi/component-library/Areas/Deposit/types'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { Button } from '@mobi/component-library/Base/Buttons'
import { PageHeader } from '@mobi/component-library/Navigation/PageHeader'
import { FeatureFlags } from '@mobi/settings/featureFlags'
import * as native from '@mobi/web-native-comms/web'
import { DepositPanel } from '.'
import { track } from '../../../Utils'
import { PaymentMethodBlocked } from '../../Deposit/Components/PaymentMethodBlocked'
import { SelfServeDepositPanels } from '../../Deposit/Components/SelfServeDepositPanels'
import type { AppProps } from '../App'
import {
  selectDepositFlow,
  selectDepositState,
  selectLastSelectedPaymentMethod,
  selectPaymentMethod,
} from '../Store'
import { selectIsBusy } from '../Store'
import { isApplePayWebSupported } from '../Utils'
import { isPaymentRequestApiSupported } from '../Utils/helpers'
import { AccountBalanceHeader } from './AccountBalanceHeader'
import { PanelStyled, PaymentMethodsStyled } from './DepositBody.styles'
import { DepositLimitStatus } from './DepositLimitStatus'

type Props = Omit<AppProps, 'flow'> & {
  initialData: InitialData
  betAccountData: BetAccount
}

type PaymentMethodStatuses = Record<PaymentMethodsAll, { available: boolean; blocked: boolean }>

export const DepositBody = ({
  accountNumber,
  accountBalance,
  initialData,
  betAccountData,
  asModal,
  onClose,
}: Props) => {
  const dispatch = useDispatch()
  const isBusy = useSelector(selectIsBusy)
  const isDepositValueVolumeLimitsEnabled = useHostContextFeature(
    FeatureFlags.DEPOSIT_VALUE_VOLUME_LIMITS.key
  )
  const flow = useSelector(selectDepositFlow)
  const lastSelectedPaymentMethod = useSelector(selectLastSelectedPaymentMethod(accountNumber))

  const { selectedPaymentMethod, depositInput } = useSelector(selectDepositState)

  const inputValue = +depositInput.inputValue
  const { depositLimits } = initialData

  const showApplePay = native.isReactNativeIos || isApplePayWebSupported()
  const showGooglePay =
    !native.isReactNativeIos && (native.isReactNativeAndroid || isPaymentRequestApiSupported())

  // When deposit is show modally, hide these payment methods
  // as their deposit flow can interrupt the user from
  // resuming their normal workflow
  const showBPay = !asModal
  const showCash = !asModal
  const showScanMyBet = !asModal && native.isReactNativeApp
  const showPayID = !asModal

  const payIdDetailList = betAccountData.PayIdDetailList ?? []
  const isPreCommitmentLimitSet = betAccountData.PreCommitmentMaxDepositFrequency !== null
  const paymentMethodStatuses: PaymentMethodStatuses = {
    CreditCard: { available: true, blocked: betAccountData.CreditCardBlockedInd === 'Y' },
    PayPal: { available: true, blocked: betAccountData.BlockPayPalInd === 'Y' },
    ApplePay: { available: showApplePay, blocked: betAccountData.BlockApplePayInd === 'Y' },
    GooglePay: {
      available: showGooglePay,
      blocked: betAccountData.BlockGooglePayInd === 'Y',
    },
    BPay: { available: showBPay, blocked: false },
    Cash: { available: showCash, blocked: false },
    ScanMyBet: { available: showScanMyBet, blocked: false },
    PayID: { available: showPayID, blocked: betAccountData.BlockEftInd === 'Y' },
  }

  const remainingDepositAmount = isDepositValueVolumeLimitsEnabled
    ? betAccountData.RemainingMaxDepositAmount
    : depositLimits.maximum
  const remainingDepositTransactions = isDepositValueVolumeLimitsEnabled
    ? betAccountData.RemainingDepositTransactions
    : Infinity

  const isAvailable = (paymentMethod: PaymentMethodsAll) =>
    paymentMethodStatuses[paymentMethod].available

  const isBlocked = (paymentMethod: PaymentMethodsAll) =>
    paymentMethodStatuses[paymentMethod].blocked

  const onPaymentMethodClick = useCallback(
    (paymentMethod: PaymentMethodsAll) => {
      dispatch(selectPaymentMethod({ accountNumber, paymentMethod }))

      // If the deposit method has changed, track it in GA
      if (paymentMethod !== selectedPaymentMethod) {
        track('deposit_method_selection', {
          accountNumber,
          paymentMethod,
          depositVariant: flow,
        })
      }
    },
    [accountNumber, dispatch, selectedPaymentMethod, flow]
  )

  useEffect(() => {
    if (selectedPaymentMethod) return

    let paymentMethod: PaymentMethodsAll = 'CreditCard'

    if (lastSelectedPaymentMethod && isAvailable(lastSelectedPaymentMethod)) {
      paymentMethod = lastSelectedPaymentMethod
    }

    dispatch(
      selectPaymentMethod({
        accountNumber,
        paymentMethod,
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPaymentMethod])

  if (!selectedPaymentMethod) {
    return null
  }
  const closeButton = (
    <Button
      onClick={onClose}
      aria-label='Close Deposit page'
      color='tertiary_dark'
      disabled={isBusy}
    >
      <Icon name='SolidXClose' />
    </Button>
  )

  const statusExcludedPaymentMethods: PaymentMethodsAll[] = ['BPay', 'Cash', 'ScanMyBet']

  return (
    <>
      <PageHeader title='Deposit' rightAlignedComponent={onClose ? closeButton : undefined} />
      <PageStyled>
        {asModal && accountBalance !== undefined && (
          <AccountBalanceHeader amount={accountBalance} />
        )}
        {!isBlocked(selectedPaymentMethod) &&
          !statusExcludedPaymentMethods.includes(selectedPaymentMethod) && (
            <DepositLimitStatus
              depositAmount={inputValue}
              isPreCommitmentLimitSet={isPreCommitmentLimitSet}
              selectedPaymentMethod={selectedPaymentMethod}
              minimumDepositAmount={depositLimits.minimum}
              remainingDepositAmount={remainingDepositAmount}
              remainingDepositTransactions={remainingDepositTransactions}
            />
          )}
        {(function () {
          const isSelectedPaymentMethodSelfServe = isSelfServePaymentMethod(selectedPaymentMethod)
          const isPaymentMethodBlocked = isBlocked(selectedPaymentMethod)

          if (isSelectedPaymentMethodSelfServe && isPaymentMethodBlocked) {
            return <PaymentMethodBlocked paymentMethod={selectedPaymentMethod} asModal={asModal} />
          }

          if (isSelectedPaymentMethodSelfServe) {
            return (
              <SelfServeDepositPanels
                accountNumber={accountNumber}
                paymentMethod={selectedPaymentMethod}
                initialData={initialData}
                isPreCommitmentLimitSet={isPreCommitmentLimitSet}
                payIdDetailList={payIdDetailList}
                remainingDepositAmount={remainingDepositAmount}
                remainingDepositTransactions={remainingDepositTransactions}
              />
            )
          }

          if (isPaymentMethodBlocked) {
            return (
              <PanelStyled>
                <PaymentMethodBlocked paymentMethod={selectedPaymentMethod} asModal={asModal} />
              </PanelStyled>
            )
          }

          return (
            <DepositPanel
              accountNumber={accountNumber}
              initialData={initialData}
              paymentMethod={selectedPaymentMethod}
              remainingDepositAmount={remainingDepositAmount}
              remainingDepositTransactions={remainingDepositTransactions}
            />
          )
        })()}
        <PaymentMethodsStyled>
          <PaymentMethods
            deviceType='mobile' // TODO detect device type
            selectedPaymentMethod={selectedPaymentMethod}
            showApplePay={isAvailable('ApplePay')}
            showGooglePay={isAvailable('GooglePay')}
            showBPay={isAvailable('BPay')}
            showCash={isAvailable('Cash')}
            showScanMyBet={isAvailable('ScanMyBet')}
            showPayID={isAvailable('PayID')}
            onClick={onPaymentMethodClick}
          />
        </PaymentMethodsStyled>
      </PageStyled>
    </>
  )
}
